if (!customElements.get('flipbook-modal')) {
  class FlipbookModal extends HTMLElement {
    constructor() {
      super();
      this.libraryPromise = null;
      this.pageFlip = null;
      this.currentImages = null;
      this.resizeHandler = null;

      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });

      const closeButton = this.querySelector('[data-flipbook-close]');
      if (closeButton) closeButton.addEventListener('click', () => this.close());

      const prevButton = this.querySelector('[data-flipbook-prev]');
      if (prevButton) prevButton.addEventListener('click', () => this.pageFlip && this.pageFlip.flipPrev());

      const nextButton = this.querySelector('[data-flipbook-next]');
      if (nextButton) nextButton.addEventListener('click', () => this.pageFlip && this.pageFlip.flipNext());

      const prevZone = this.querySelector('[data-flipbook-click-prev]');
      if (prevZone) prevZone.addEventListener('click', () => this.pageFlip && this.pageFlip.flipPrev());

      const nextZone = this.querySelector('[data-flipbook-click-next]');
      if (nextZone) nextZone.addEventListener('click', () => this.pageFlip && this.pageFlip.flipNext());

      document.addEventListener('keydown', (event) => {
        if (!this.classList.contains('flipbook-modal--open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft' && this.pageFlip) this.pageFlip.flipPrev();
        if (event.key === 'ArrowRight' && this.pageFlip) this.pageFlip.flipNext();
      });

      document.querySelectorAll('[data-flip-images]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const { flipImages, flipTitle, flipFile, flipWidth, flipHeight } = trigger.dataset;
          const images = flipImages
            .split(',')
            .map((url) => url.trim())
            .filter(Boolean);
          this.open(images, flipTitle, flipFile, Number(flipWidth) || 1, Number(flipHeight) || 1);
        });
      });
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          if (existing.dataset.loaded) return resolve();
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }

    loadLibrary() {
      if (!this.libraryPromise) {
        this.libraryPromise = this.loadScript(this.dataset.pageFlipUrl);
      }
      return this.libraryPromise;
    }

    setStatus(text, showDownload) {
      const status = this.querySelector('[data-flipbook-status]');
      if (status) status.textContent = text;
      const downloadLink = this.querySelector('[data-flipbook-fallback-download]');
      if (downloadLink) downloadLink.hidden = !showDownload;
    }

    // Fit an aspect-ratio box (imgWidth x imgHeight) as large as possible inside the
    // available body area, the same math as CSS object-fit: contain.
    sizeStage(imgWidth, imgHeight) {
      const wrapper = this.querySelector('[data-flipbook-stage-wrapper]');
      const body = this.querySelector('.flipbook-modal__body');
      const bodyRect = body.getBoundingClientRect();
      const ratio = imgWidth / imgHeight;

      let width = bodyRect.width;
      let height = width / ratio;
      if (height > bodyRect.height) {
        height = bodyRect.height;
        width = height * ratio;
      }

      width = Math.max(1, Math.round(width));
      height = Math.max(1, Math.round(height));
      wrapper.style.width = `${width}px`;
      wrapper.style.height = `${height}px`;
      return { width, height };
    }

    async open(images, title, downloadUrl, imgWidth, imgHeight) {
      if (!images || !images.length) return;
      this.classList.add('flipbook-modal--open');
      document.body.classList.add('flipbook-modal-open');
      this.querySelector('[data-flipbook-title]').textContent = title || '';

      const downloadLink = this.querySelector('[data-flipbook-fallback-download]');
      if (downloadLink) {
        downloadLink.href = downloadUrl || images[0];
        downloadLink.hidden = true;
      }

      const imagesKey = images.join('|');
      const navControls = this.querySelectorAll(
        '[data-flipbook-prev], [data-flipbook-next], [data-flipbook-click-prev], [data-flipbook-click-next]'
      );

      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
      this.resizeHandler = () => this.sizeStage(imgWidth, imgHeight);
      window.addEventListener('resize', this.resizeHandler);

      if (this.currentImages === imagesKey && this.pageFlip) {
        this.sizeStage(imgWidth, imgHeight);
        this.querySelector('[data-flipbook-stage]').hidden = false;
        this.setStatus('');
        navControls.forEach((el) => (el.hidden = false));
        return;
      }

      this.setStatus(window.flipbookStrings ? window.flipbookStrings.loading : 'Loading...');
      navControls.forEach((el) => (el.hidden = true));

      try {
        await this.loadLibrary();

        // PageFlip.destroy() removes its target element from the DOM entirely
        // (not just its contents), so the old stage element can't be reused -
        // swap in a fresh one each time we rebuild.
        if (this.pageFlip) {
          this.pageFlip.destroy();
        }
        const wrapper = this.querySelector('[data-flipbook-stage-wrapper]');
        const oldStage = wrapper.querySelector('[data-flipbook-stage]');
        const stage = document.createElement('div');
        stage.className = 'flipbook-modal__stage';
        stage.setAttribute('data-flipbook-stage', '');
        if (oldStage) {
          oldStage.replaceWith(stage);
        } else {
          wrapper.prepend(stage);
        }

        // PageFlip measures the container's size synchronously at construction time,
        // so it must already be visible (not [hidden]) before we instantiate it.
        // Dawn's base.css also hides *:empty elements, and the stage has no children
        // yet at this point, so give it a throwaway placeholder to keep it out of
        // that rule until PageFlip replaces the contents itself.
        stage.hidden = false;
        stage.textContent = ' ';

        const { width, height } = this.sizeStage(imgWidth, imgHeight);

        this.pageFlip = new window.St.PageFlip(stage, {
          width,
          height,
          size: 'fixed',
          maxShadowOpacity: 0.4,
          showCover: false,
          mobileScrollSupport: true,
          useMouseEvents: false,
        });
        this.pageFlip.loadFromImages(images);
        this.currentImages = imagesKey;

        this.setStatus('');
        navControls.forEach((el) => (el.hidden = false));
      } catch (error) {
        this.setStatus(window.flipbookStrings ? window.flipbookStrings.error : 'Could not load this document.', true);
        // eslint-disable-next-line no-console
        console.error('Flipbook error', error);
      }
    }

    close() {
      this.classList.remove('flipbook-modal--open');
      document.body.classList.remove('flipbook-modal-open');
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
    }
  }

  customElements.define('flipbook-modal', FlipbookModal);
}
